import type { AIPresenceState } from '@/common/components/presence/types';
import {
  getNebulaChoreography,
  resolveSpeakingFlowRate,
} from '@/common/components/presence/nebula/nebulaChoreography';
import {
  buildFormationLobeSpecs,
  liftNebulaRgb,
  mixNebulaRgb,
  nebulaContourNoise,
  parseNebulaRgb,
} from '@/common/components/presence/nebula/nebulaContourGeometry';
import {
  buildNebulaVisualFrame,
  getNebulaFormationPaintRgb,
  nebulaWellAtmosphereRgb,
  type NebulaFormation,
  type NebulaVisualFrame,
} from '@/common/components/presence/nebula/nebulaVisualModel';
import {
  computePulseCanvasCssSize,
  computeSpeakingDrawScale,
} from '@/common/components/presence/presenceGlowBudget';
import type { OrbPaletteStops } from '@/common/theme/orbPalette';

export type NebulaCanvasContext = CanvasRenderingContext2D;

function drawFormationBlob(
  ctx: NebulaCanvasContext,
  formation: NebulaFormation,
  palette: OrbPaletteStops,
  cx: number,
  cy: number,
  radius: number,
  size: number,
  colorLift: number,
) {
  const [r, g, b] = liftNebulaRgb(
    getNebulaFormationPaintRgb(palette[formation.cloudFamily]),
    colorLift,
  );
  const [hr, hg, hb] = liftNebulaRgb([r, g, b], 0.18);
  const [sr, sg, sb] = [
    Math.round(r * 0.58),
    Math.round(g * 0.58),
    Math.round(b * 0.58),
  ];
  const px = cx + formation.centerX * radius;
  const py = cy + formation.centerY * radius;
  const baseRx = formation.scaleX * radius * 0.67;
  const baseRy = formation.scaleY * radius * 0.67;

  ctx.save();
  ctx.translate(px, py);
  ctx.rotate(formation.rotation);
  ctx.globalCompositeOperation = 'source-over';
  ctx.filter = `blur(${size <= 28 ? 1.2 : Math.max(4, size * 0.038)}px)`;

  // A gradient peaking at the parcel's own centre gives it a nucleus, and a
  // nucleus with a soft radial falloff is exactly what reads as a cotton ball.
  // So the falloff is anchored off-centre and the top is nearly a plateau —
  // local density comes from the lobes, not from a bright middle.
  const outerR = Math.max(baseRx, baseRy);
  const focusAngle = formation.phase * 0.6 + formation.seed * 0.41;
  const fieldGradient = ctx.createRadialGradient(
    Math.cos(focusAngle) * baseRx * 0.36,
    Math.sin(focusAngle) * baseRy * 0.36,
    outerR * 0.05,
    0,
    0,
    outerR,
  );
  fieldGradient.addColorStop(0, `rgba(${hr},${hg},${hb},${formation.alpha * 0.32})`);
  fieldGradient.addColorStop(0.44, `rgba(${r},${g},${b},${formation.alpha * 0.28})`);
  fieldGradient.addColorStop(0.74, `rgba(${sr},${sg},${sb},${formation.alpha * 0.14})`);
  fieldGradient.addColorStop(1, `rgba(${r},${g},${b},0)`);
  ctx.fillStyle = fieldGradient;
  ctx.beginPath();
  for (let i = 0; i < 14; i += 1) {
    const angle = (i / 14) * Math.PI * 2;
    const roughness = 1 + (nebulaContourNoise(angle, formation.phase, formation.seed) - 0.58)
      * formation.contourRoughness * 2.4;
    const x = Math.cos(angle) * baseRx * roughness;
    const y = Math.sin(angle) * baseRy * roughness;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();

  buildFormationLobeSpecs(formation, radius, size).forEach((lobe, index) => {
    const gradient = ctx.createRadialGradient(lobe.ox, lobe.oy, 0, lobe.ox, lobe.oy, Math.max(lobe.lrx, lobe.lry));
    const lobeTone = index % 4 === 0
      ? [hr, hg, hb]
      : index % 4 === 2
        ? [sr, sg, sb]
        : [r, g, b];
    const [lr, lg, lb] = lobeTone;
    gradient.addColorStop(0, `rgba(${lr},${lg},${lb},${lobe.alpha * 0.48})`);
    gradient.addColorStop(0.46, `rgba(${r},${g},${b},${lobe.alpha * 0.26})`);
    gradient.addColorStop(0.76, `rgba(${sr},${sg},${sb},${lobe.alpha * 0.08})`);
    gradient.addColorStop(1, `rgba(${r},${g},${b},0)`);
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.ellipse(lobe.ox, lobe.oy, lobe.lrx, lobe.lry, lobe.rotation, 0, Math.PI * 2);
    ctx.fill();
  });

  ctx.restore();
}

export function drawNebulaCanvasFrame(
  ctx: NebulaCanvasContext,
  frame: NebulaVisualFrame,
  palette: OrbPaletteStops,
  size: number,
) {
  const cx = size / 2;
  const cy = size / 2;
  const radius = frame.silhouetteRadius;
  const well = parseNebulaRgb(palette.well);
  const atmosphere = nebulaWellAtmosphereRgb(palette, frame.luminosity);
  const liftedWell = mixNebulaRgb(well, atmosphere, frame.wellColorLift);
  const wellHighlight = mixNebulaRgb(
    well,
    liftNebulaRgb(atmosphere, 0.16),
    Math.min(1, frame.wellColorLift + 0.18),
  );
  const wellDeep = mixNebulaRgb(well, [0, 0, 0], 0.42);
  const wellCx = cx + (frame.wellCenterX - 0.5) * radius * 0.9;
  const wellCy = cy + (frame.wellCenterY - 0.5) * radius * 0.9;

  ctx.clearRect(0, 0, size, size);

  if (frame.haloAlpha > 0) {
    const [hr, hg, hb] = palette.haloRgb;
    const halo = ctx.createRadialGradient(cx, cy, radius * 0.2, cx, cy, radius * 1.35);
    halo.addColorStop(0, `rgba(${hr},${hg},${hb},${frame.haloAlpha})`);
    halo.addColorStop(1, `rgba(${hr},${hg},${hb},0)`);
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, size, size);
  }

  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.clip();

  const wellGradient = ctx.createRadialGradient(
    wellCx,
    wellCy,
    radius * 0.04,
    wellCx,
    wellCy,
    radius,
  );
  wellGradient.addColorStop(
    0,
    `rgba(${wellHighlight[0]},${wellHighlight[1]},${wellHighlight[2]},${frame.wellAlpha})`,
  );
  wellGradient.addColorStop(
    0.58,
    `rgba(${liftedWell[0]},${liftedWell[1]},${liftedWell[2]},${frame.wellAlpha})`,
  );
  wellGradient.addColorStop(1, `rgba(${wellDeep[0]},${wellDeep[1]},${wellDeep[2]},1)`);
  ctx.fillStyle = wellGradient;
  ctx.fillRect(0, 0, size, size);

  frame.formations.forEach((formation) => {
    drawFormationBlob(ctx, formation, palette, cx, cy, radius, size, frame.cloudColorLift);
  });

  if (frame.coreAlpha > 0.02) {
    const core = parseNebulaRgb(palette.core);
    const coreGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius * 0.2);
    coreGradient.addColorStop(
      0,
      `rgba(${core[0]},${core[1]},${core[2]},${frame.coreAlpha})`,
    );
    coreGradient.addColorStop(1, `rgba(${core[0]},${core[1]},${core[2]},0)`);
    ctx.globalCompositeOperation = 'screen';
    ctx.fillStyle = coreGradient;
    ctx.fillRect(0, 0, size, size);
  }

  const innerShadow = ctx.createRadialGradient(cx, cy, radius * 0.88, cx, cy, radius);
  innerShadow.addColorStop(0, 'rgba(0,0,0,0)');
  innerShadow.addColorStop(0.55, `rgba(0,0,0,${frame.innerShadowStrength * 0.35})`);
  innerShadow.addColorStop(1, `rgba(0,0,0,${Math.min(frame.innerShadowStrength + 0.12, 0.72)})`);
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = innerShadow;
  ctx.fillRect(0, 0, size, size);
  ctx.restore();

  const rim = parseNebulaRgb(palette.rim);
  const rimGradient = ctx.createLinearGradient(
    cx - radius,
    cy - radius,
    cx + radius * 0.6,
    cy + radius,
  );
  rimGradient.addColorStop(0, `rgba(151,190,202,${frame.rimAlpha * 0.48})`);
  rimGradient.addColorStop(
    0.5,
    `rgba(${rim[0]},${rim[1]},${rim[2]},${frame.rimAlpha * 0.72})`,
  );
  rimGradient.addColorStop(1, `rgba(91,122,136,${frame.rimAlpha * 0.85})`);
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius - 0.5, 0, Math.PI * 2);
  ctx.strokeStyle = rimGradient;
  ctx.lineWidth = Math.max(1, size * 0.022);
  ctx.stroke();
  ctx.restore();
}

export type NebulaCanvasAnimator = {
  drawNow: (at: number) => void;
  setVisible: (visible: boolean) => void;
  dispose: () => void;
};

type CreateAnimatorParams = {
  canvas: HTMLCanvasElement;
  size: number;
  palette: OrbPaletteStops;
  state: () => AIPresenceState;
  pulseBoost: (at: number) => number;
  speechAmp: () => number;
  /** Peak-hold punch — accelerates the field, see resolveSpeakingFlowRate. */
  speechPunch?: () => number;
  now: () => number;
  requestFrame: (callback: FrameRequestCallback) => number;
  cancelFrame: (id: number) => void;
  devicePixelRatio: number;
};

/**
 * Owns one initialized canvas surface sized for the peak speaking pulse.
 * Visibility only pauses/resumes RAF; it never resizes the backing store, so
 * the last painted frame survives IO churn. Punch scales the 2D CTM inside
 * that peak buffer — CSS must not scale the bitmap.
 */
export function createNebulaCanvasAnimator(params: CreateAnimatorParams): NebulaCanvasAnimator {
  const {
    canvas,
    size,
    palette,
    state,
    pulseBoost,
    speechAmp,
    speechPunch = () => 0,
    now,
    requestFrame,
    cancelFrame,
    devicePixelRatio,
  } = params;
  const dpr = Math.min(devicePixelRatio || 1, 2);
  const canvasCss = computePulseCanvasCssSize(size);
  const pixelSize = Math.round(canvasCss * dpr);
  if (canvas.width !== pixelSize) canvas.width = pixelSize;
  if (canvas.height !== pixelSize) canvas.height = pixelSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return {
      drawNow: () => undefined,
      setVisible: () => undefined,
      dispose: () => undefined,
    };
  }

  const startedAt = now();
  let raf = 0;
  let isVisible = false;
  let disposed = false;
  let lastFrameAt = 0;
  let flowMs = 0;
  let flowClockAt = startedAt;

  const applySpeakingDrawTransform = (scale: number) => {
    // Clear in device space so a shrinking pulse does not leave a ghost ring.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const offsetCss = (canvasCss - size * scale) / 2;
    ctx.setTransform(
      dpr * scale,
      0,
      0,
      dpr * scale,
      offsetCss * dpr,
      offsetCss * dpr,
    );
  };

  const drawNow = (at: number) => {
    if (disposed) return;
    const currentState = state();
    // The field runs on an integrated clock, not wall time, so a speech punch
    // can accelerate it — each peak kicks the fog forward, then it coasts back.
    // Clamped so a backgrounded tab does not resume with one huge jump.
    const deltaMs = Math.min(Math.max(at - flowClockAt, 0), 250);
    flowClockAt = at;
    flowMs += deltaMs * resolveSpeakingFlowRate(currentState, speechPunch());
    const drawScale = computeSpeakingDrawScale(currentState, speechPunch());
    applySpeakingDrawTransform(drawScale);
    const frame = buildNebulaVisualFrame({
      state: currentState,
      size,
      elapsedMs: flowMs,
      pulseBoost: pulseBoost(at),
      speechAmp: speechAmp(),
    });
    drawNebulaCanvasFrame(ctx, frame, palette, size);
  };

  const tick = (at: number) => {
    if (disposed || !isVisible) return;
    const minFrameMs = 1000 / getNebulaChoreography(state()).maxFps;
    if (at - lastFrameAt >= minFrameMs) {
      lastFrameAt = at;
      drawNow(at);
    }
    raf = requestFrame(tick);
  };

  const setVisible = (visible: boolean) => {
    if (disposed || visible === isVisible) return;
    isVisible = visible;
    if (!visible) {
      if (raf) cancelFrame(raf);
      raf = 0;
      return;
    }
    drawNow(now());
    raf = requestFrame(tick);
  };

  drawNow(startedAt);

  return {
    drawNow,
    setVisible,
    dispose: () => {
      disposed = true;
      if (raf) cancelFrame(raf);
      raf = 0;
    },
  };
}
